import React from "react";
import { DispatchProp } from "react-redux";
import { QuestionModel } from "../../models/QuestionModel";
import { QuestionType, isGrid } from "../../models/enums/QuestionType";
import { BaseControl, ControlProps } from "./BaseControl";
import {
    getSettingNumber,
    getSettingBoolean,
    getSettingString,
} from "../../models/utilities/settingUtilities";
import { ChoiceModel } from "../../models/ChoiceModel";
import { isNullOrEmpty } from "../../utils";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { hasOpenEnd, isVisible } from "../../models/utilities/choiceUtilities";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import ChoiceListRenderer from "../renderers/v2/ChoiceListRenderer";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import {
    isCarrouselCheckboxMode,
    isCarrouselScaleMode,
} from "../../models/utilities/carrouselUtilities";

interface Props extends ControlProps, DispatchProp {}

interface State {
    openEndValue: { [key: string]: string };
}

export class ChoiceListControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { openEndValue: {} };
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        const variable = this.setCarrouselActiveVariable(question.id) || question.variables[0];
        const { choiceList = [] } = variable;

        if (!this.hasMentions(props)) {
            switch (question.type) {
                case QuestionType.RespondentTimeZone: {
                    const choice = choiceList.find((answer) => answer.value === props.timeZone);
                    if (choice) {
                        this.setMentions(
                            props,
                            variable.name,
                            (mentions) => mentions.set(choice.value),
                            undefined,
                            true,
                        );
                    }
                    break;
                }
                default: {
                    this.setDefaultChoices();
                    break;
                }
            }
        }
        this.setState((s, p) => {
            const newState = { ...s };
            choiceList.forEach((choice) => {
                const mention = this.getMentions(p, variable.name).find(
                    (m) => m.value === choice.value,
                );
                if (mention !== undefined && !isNullOrEmpty(mention.openEnd)) {
                    newState.openEndValue = {
                        ...newState.openEndValue,
                        [choice.index]: mention.openEnd || "",
                    };
                } else {
                    newState.openEndValue = { ...newState.openEndValue, [choice.index]: "" };
                }
            });
            return newState;
        });
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        super.componentDidUpdate(prevProps, prevState);
        const variable =
            this.setCarrouselActiveVariable(this.props.question.id) ||
            this.props.question.variables[0];

        const mentions = this.getMentions(this.props);
        mentions.forEach((mention) => {
            const isValidChoice = this.isValidChoice(mention.value);
            const isChoiceNoLongerVisible = this.isChoiceNoLongerVisible(mention.value, prevProps);
            if (isValidChoice && isChoiceNoLongerVisible) {
                this.setMentions(this.props, variable.name, (m) => m.pull(mention.value));
            }
        });
    }

    willUpdateMentions(question: QuestionModel, nextProps: Props) {
        super.willUpdateMentions(question, nextProps);
        const variable = this.setCarrouselActiveVariable(question.id) || question.variables[0];
        const mentions = this.getMentions(nextProps, variable.name) || [];
        this.setState((s) => {
            const newState = { ...s };
            variable.choiceList.forEach((answer) => {
                const mention = mentions.find((m) => m.value === answer.value);
                if (mention !== undefined) {
                    newState.openEndValue = {
                        ...newState.openEndValue,
                        [answer.index]: mention.openEnd,
                    };
                }
            });
            return newState;
        });
    }

    onReset = () => {
        const { question } = this.props;
        const variable = this.setCarrouselActiveVariable(question.id) || question.variables[0];
        this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
    };

    onOpenEndChange = (choice: ChoiceModel, text: string) => {
        this.setState((s) => {
            const newState = { ...s };
            newState.openEndValue = {
                ...newState.openEndValue,
                [choice.index]: this.truncateOpenEnd(choice, text),
            };
            return newState;
        });
    };

    onOpenEndBlur = (choice: ChoiceModel) => {
        const openEnd = this.state.openEndValue[choice.index];
        const [variable] = this.props.question.variables;
        this.setMentions(this.props, variable.name, (mentions) =>
            mentions.removeValue(choice.value).add(choice.value, openEnd.trim()),
        );
    };

    setDefaultChoices = () => {
        const { sequencedVariables } = this.props;
        const [variable] = sequencedVariables;
        const { choiceList = [] } = variable;
        const defaultVisibleChoices = choiceList.filter(
            (choice) => getSettingBoolean(choice, "Default") && isVisible(choice),
        );
        this.setChoicesAsMentions(variable, defaultVisibleChoices);
    };

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { question, useClientValidation } = this.props;

        question.variables.forEach(this.removeUnavailableMentions);
        if (question.type === QuestionType.RadioButton) {
            return new ValidationBuilder().build();
        }

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const softPromptValidationResult = this.validateSoftPrompt(this.props);
        if (!softPromptValidationResult.isValid) {
            return softPromptValidationResult;
        }

        const result = super.validate(options);
        const variable = this.setCarrouselActiveVariable(question.id) || question.variables[0];

        const hasMentions = this.hasMentions(this.props);

        if (!hasMentions) {
            const { choiceList } = variable;
            const invisibleDefaultChoices = choiceList.filter(
                (choice) => getSettingBoolean(choice, "Default") && !isVisible(choice),
            );
            if (invisibleDefaultChoices.length) {
                this.setChoicesAsMentions(variable, invisibleDefaultChoices);
                return new ValidationBuilder().build();
            }
        }

        if (result.isValid && hasMentions) {
            const min = this.minValue();
            const max = this.maxValue();
            const mentions = this.getMentions(this.props);

            // Test for minimum and maximum values.
            if (min !== null && min > 0 && mentions.length < min) {
                return new ValidationBuilder()
                    .fail(variable.name, "InvalidChoicesMin", { min: String(min) })
                    .build();
            }
            if (max !== null && max > 0 && mentions.length > max) {
                return new ValidationBuilder()
                    .fail(variable.name, "InvalidChoicesMax", { max: String(max) })
                    .build();
            }

            // Validate open end if parent is not Grid
            if (!question.parent || !isGrid(question.parent.type)) {
                const { choiceList } = variable;
                const choicesWithOpenEnd = choiceList.filter(hasOpenEnd);

                const openEndFailedValidation = choicesWithOpenEnd
                    .map((choice) => {
                        const openEnd = this.state.openEndValue[choice.index];
                        const mention = mentions.find((m) => m.value === choice.value);
                        return mention ? this.validateOpenEnd(openEnd, choice) : undefined;
                    })
                    .find((validation) => validation && !validation.isValid);

                if (openEndFailedValidation) {
                    return openEndFailedValidation;
                }
            }
        }
        return result;
    }

    renderQuestion(question: QuestionModel) {
        const variable =
            this.setCarrouselActiveVariable(question.id) || this.props.sequencedVariables[0];
        const choiceList = variable ? variable.choiceList : [];
        return (
            <ChoiceListRenderer
                {...getRendererProps(this.props)}
                openEndValue={this.state.openEndValue}
                gridRendering={this.props.gridRendering}
                onOpenEndChange={this.onOpenEndChange}
                onOpenEndBlur={this.onOpenEndBlur}
                choices={choiceList}
                onReset={this.onReset}
                language={this.props.language}
                mentions={this.getMentions(this.props)}
                variable={variable}
                maxAnswers={getSettingNumber(question, "MaxAllowedAnswers")}
                isCarrouselCheckboxActive={isCarrouselCheckboxMode(question)}
                isCarrouselScaleActive={isCarrouselScaleMode(question)}
                onAfterSelection={this.props.onAfterSelection}
                executeUpdateForIdAfterSelection={this.props.executeUpdateForIdAfterSelection}
            />
        );
    }
}
